import { useState } from "react";

import {
  useCurrentProjectId,
  useMethodologyList,
  useMethodologyUpgradePreview,
  useMethodologyVersion,
  useMigrateMethodology,
} from "@/api/queries";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { Field } from "@/components/Field";
import { Panel } from "@/components/Panel";
import { RuleDiffList } from "@/components/RuleDiffList";
import { Select } from "@/components/Select";
import { StatusLabel } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";
import { formatNumber } from "@/lib/cn";
import { useUiStore } from "@/store/ui";

/** 把 `name@version` 引用拆开；没有 `@` 时只当作版本号。 */
function parseRef(ref: string | null | undefined): { name: string | null; version: string | null } {
  if (!ref) return { name: null, version: null };
  const at = ref.indexOf("@");
  if (at < 0) return { name: null, version: ref };
  return { name: ref.slice(0, at), version: ref.slice(at + 1) };
}

/**
 * 方法论版本包（specs/14 §4.10 · ADR-004）。
 *
 * 四件事在这里落地：
 * - `GET /methodologies` —— 当前版本 + 已注册版本包清单；
 * - `GET /methodologies/{name}/{version}` —— 单版本详情（**GET**，不是 POST）；
 * - `POST /projects/{id}/methodology/preview` —— 升级影响预览；
 * - `POST /projects/{id}/methodology/migrate` —— 人工批准后执行迁移。
 *
 * 升级目标只在本包内换版本（路由只收 `target_version`），所以选项里不含换包。
 * 预览结果里 `version_changed` 为假即"目标就是当前版本"，此时不给迁移入口——
 * 迁移按钮的存在条件是**预览已跑且确有变化且允许自动升级**，三者缺一不可。
 *
 * 预览的结论分两类，都必须呈现：
 * - **规则差异**（`rule_diff`）——由 `RuleDiffList` 渲染，它守着「没比成 ≠ 没变化」的判据；
 * - **需重新校验的产物**（`affected_artifacts`）——产物自己记的引用与目标不一致的那些。
 *
 * 刻意**不**把 `rule_diff.available === false` 做成按钮禁用条件：那会把
 * "目标版本根本没发布"这个**数据问题**藏进一个灰掉的按钮里。判据该由后端决定
 * （AGENTS §3：状态合法性一律由后端确定性校验器决定），前端只负责把它说出来。
 */
export function MethodologyPanel() {
  const projectId = useCurrentProjectId();
  const pushToast = useUiStore((state) => state.pushToast);

  const list = useMethodologyList();
  const upgradePreview = useMethodologyUpgradePreview(projectId);
  const migrate = useMigrateMethodology(projectId);

  const current = list.data?.current;
  const registered = list.data?.registered ?? [];

  /** 升级目标：默认当前版本（"升级到自身"是合法起点，预览会如实说无变化）。 */
  const [target, setTarget] = useState("");
  /** 正在查看详情的引用；默认跟随当前版本。 */
  const [detailRef, setDetailRef] = useState<string | null>(null);

  const effectiveTarget = target || current?.version || "";
  const viewingRef = detailRef ?? current?.ref ?? null;
  const { name: detailName, version: detailVersion } = parseRef(viewingRef);
  const detail = useMethodologyVersion(detailName, detailVersion);

  const preview = upgradePreview.data;
  const changed = Boolean(preview && (preview.name_changed || preview.version_changed));

  const targetOptions = [
    ...(current ? [{ value: current.version, label: `${current.version}（当前）` }] : []),
    ...registered
      .filter((item) => item.version !== current?.version)
      .map((item) => ({ value: item.version, label: `${item.version}（已注册）` })),
  ];

  function runPreview() {
    upgradePreview.mutate(effectiveTarget, {
      onError: (error) =>
        pushToast({
          title: "预览失败",
          detail: error instanceof Error ? error.message : "未知错误",
          mark: "!",
        }),
    });
  }

  function runMigrate() {
    migrate.mutate(effectiveTarget, {
      onSuccess: (result) => {
        pushToast({
          title: result.migrated ? "方法论已迁移" : "无需迁移（目标即当前版本）",
          detail: `${result.from} → ${result.to} · 影响 ${result.affected_artifacts.length} 个产物`,
          mark: "✓",
        });
        upgradePreview.reset();
      },
      onError: (error) =>
        pushToast({
          title: "迁移失败",
          detail: error instanceof Error ? error.message : "未知错误",
          mark: "!",
        }),
    });
  }

  return (
    <Panel
      title="方法论版本包"
      description="ADR-004：升级需人工批准；已进入正式剧本阶段的项目不自动升级"
    >
      <div className="meta-row" style={{ marginBottom: 14 }}>
        <StatusLabel tone={list.isError ? "danger" : "success"}>
          {list.isError ? "版本清单不可用" : `当前 ${current?.ref ?? "—"}`}
        </StatusLabel>
        <Tag>
          {registered.length === 0 ? "无其它已注册版本" : `已注册 ${registered.length} 个版本`}
        </Tag>
      </div>

      {registered.length > 0 ? (
        <ul className="activity-list" style={{ marginBottom: 14 }}>
          {registered.map((item) => (
            <li className="activity-item" key={item.ref}>
              <span className="mini-mark">◇</span>
              <div>
                <p>{item.ref}</p>
                <small>{item.name}</small>
              </div>
              <Button
                size="small"
                variant="quiet"
                onClick={() => setDetailRef(item.ref === current?.ref ? null : item.ref)}
              >
                {item.ref === viewingRef ? "回到当前" : "查看详情"}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="inline-note" style={{ marginBottom: 14 }}>
          尚未注册其它版本包；下方预览的目标因此只有当前版本。
        </p>
      )}

      <div className="section-title">
        <div>
          <h3>版本详情</h3>
          <p>{viewingRef ?? "—"}</p>
        </div>
      </div>
      {detail.isError ? (
        <p className="inline-note" style={{ color: "var(--red)" }}>
          版本详情加载失败：
          {detail.error instanceof Error ? detail.error.message : "未知错误"}
        </p>
      ) : detail.data ? (
        <DefinitionList
          entries={[
            { term: "状态", value: detail.data.status },
            { term: "规则数", value: formatNumber(detail.data.rules.length) },
            {
              term: "枚举组",
              value: Object.entries(detail.data.enums)
                .map(([key, values]) => `${key}(${values.length})`)
                .join(" · "),
            },
            { term: "Schema", value: detail.data.schemas.join(" · ") || "—" },
            { term: "解析顺序", value: detail.data.resolution_order.join(" → ") },
            { term: "变更说明", value: detail.data.changelog || "—" },
          ]}
        />
      ) : (
        <p className="inline-note">加载中…</p>
      )}

      <div className="section-title" style={{ marginTop: 20 }}>
        <div>
          <h3>升级影响预览</h3>
          <p>目标版本与当前版本比较后，再决定是否迁移</p>
        </div>
      </div>

      <Field id="methodology-target" label="目标版本">
        <Select
          id="methodology-target"
          value={effectiveTarget}
          options={targetOptions}
          onChange={(event) => setTarget(event.target.value)}
        />
      </Field>

      <div className="toolbar-right" style={{ marginTop: 12 }}>
        <Button
          size="small"
          variant="quiet"
          id="methodology-preview"
          disabled={!effectiveTarget || upgradePreview.isPending}
          onClick={runPreview}
        >
          {upgradePreview.isPending ? "预览中…" : "预览影响"}
        </Button>
        <Button
          size="small"
          variant="primary"
          id="methodology-migrate"
          disabled={!changed || !preview?.auto_upgrade_allowed || migrate.isPending}
          onClick={runMigrate}
        >
          {migrate.isPending ? "迁移中…" : "执行迁移"}
        </Button>
      </div>

      {preview ? (
        <div style={{ marginTop: 14 }}>
          <DefinitionList
            entries={[
              { term: "从", value: preview.from },
              { term: "到", value: preview.to },
              { term: "包名变化", value: preview.name_changed ? "是" : "否" },
              { term: "版本变化", value: preview.version_changed ? "是" : "否" },
              { term: "当前阶段", value: preview.current_stage ?? "—" },
              {
                term: "允许自动升级",
                value: preview.auto_upgrade_allowed ? "是" : "否（当前阶段禁止）",
              },
              {
                term: "需人工批准",
                value: preview.requires_manual_approval ? "是" : "否",
              },
              { term: "说明", value: preview.note },
              {
                // 判据是产物自己记的 methodology_version，不是依赖图；
                // 空数组的含义是「没有产物记在旧引用下」，可以直接说出口。
                term: "需重新校验的产物",
                value:
                  preview.affected_artifacts.length === 0
                    ? "无"
                    : preview.affected_artifacts.join(" · "),
              },
            ]}
          />
          {/* 规则差异：能不能比由 `diff.available` 决定，见 RuleDiffList 顶部说明。 */}
          <RuleDiffList diff={preview.rule_diff} />
          {!changed ? <p className="inline-note">目标就是当前版本，没有需要迁移的变化。</p> : null}
        </div>
      ) : null}
    </Panel>
  );
}
